import { MoreVert } from "@mui/icons-material";
import { Box, Typography, Chip, useTheme, IconButton, Menu, MenuItem, Checkbox, ListItemText, CircularProgress } from "@mui/material";
import { CaseResponseDto, PagedResponse } from "@bandido/shared/dtos";
import { useEffect, useState } from "react";
import { addLeadingZeros } from "@frontend/features/common/utils/helpers";
import { defaultLeadingZeros } from "@frontend/features/common/constants";
import { useWhatsappClient } from "../WhatsappClientContext";
import { useTranslation } from "react-i18next";
import { MessageResponse } from "../../types";
import { ChatMessage } from "../ChatMessage";
import { ChatSticker } from "../ChatSticker";
import { ChatAudio } from "../ChatAudio";
import { ChatImage } from "../ChatImage";
import { ChatVideo } from "../ChatVideo";
import { ChatContact } from "../ChatContact";
import { ChatDocument } from "../ChatDocument";
import { ChatLocation } from "../ChatLocation";
import { getMessagePresentation } from '../../messagePresentation';

interface Props {
  casesData?: PagedResponse<CaseResponseDto>;
  children: React.ReactNode;
  backgroundColor?: string;
  textColor?: string;
  currentCaseId?: number;
}

type ChatBaseProps = MessageResponse & Props

export const ChatBase: React.FC<ChatBaseProps> = (data) => {
  const {
    createdAt,
    inbound,
    casesData,
    cases,
    children,
    backgroundColor,
    textColor,
    status,
    contextMessage,
    currentCaseId,
  } = data;
  const time = new Date(createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const theme = useTheme();
  const { t } = useTranslation();
  const presentation = getMessagePresentation(inbound);
  const isInbound = presentation.attribution === 'client';
  const isOutbound = presentation.attribution === 'agency';
  const isUnknown = presentation.attribution === 'unknown';
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCaseIds, setSelectedCaseIds] = useState<number[]>(cases ?? []);
  const [pendingCaseIds, setPendingCaseIds] = useState<Set<number>>(new Set());
  const { handleLinkMessageToCase, handleUnlinkMessageFromCase, setMessageToReply } = useWhatsappClient();

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    setSelectedCaseIds(cases ?? []);
  }, [cases]);

  const isPending = (caseId: number) => pendingCaseIds.has(caseId);

  const addPending = (caseId: number) => {
    setPendingCaseIds((prev) => {
      const next = new Set(prev);
      next.add(caseId);
      return next;
    });
  };

  const removePending = (caseId: number) => {
    setPendingCaseIds((prev) => {
      const next = new Set(prev);
      next.delete(caseId);
      return next;
    });
  };

  const onCaseToggle = async (caseId: number) => {
    if (isPending(caseId)) return;
    const isLinked = selectedCaseIds.includes(caseId);
    addPending(caseId);
    setSelectedCaseIds((prev) =>
      isLinked ? prev.filter((id) => id !== caseId) : [...prev, caseId],
    );
    try {
      if (isLinked) {
        await handleUnlinkMessageFromCase(data.id, caseId);
      } else {
        await handleLinkMessageToCase(data.id, caseId);
      }
    } catch (error) {
      console.error('Failed to toggle case link for message', error);
      setSelectedCaseIds((prev) =>
        isLinked ? [...prev, caseId] : prev.filter((id) => id !== caseId),
      );
    } finally {
      removePending(caseId);
    }
  };

  const handleReplyMessage = () => {
    if (!data) return;
    setMessageToReply(data);
    setAnchorEl(null);
  };

  const renderContextMessage = () => {
    if (!contextMessage) return null;

    const sender =
      contextMessage.senderName === "BANDIDO_SYSTEM" ? t('whatsapp.systemSender') : contextMessage.senderName;

    const preview = (() => {
      const msg = contextMessage;
      switch (msg.type) {
        case 'text':
          return <ChatMessage message={msg.text || ''} />;
        case 'contacts':
          return <ChatContact message={msg.text || ''} />;
        case 'template':
          if (msg.caption) {
            switch (msg.caption) {
              case 'image':
                return msg.text ? <ChatImage url={msg.text} size="compact" /> : null;
              case 'video':
                return msg.text ? <ChatVideo fileId={msg.text} size="compact" /> : null;
              case 'audio':
                return msg.text ? <ChatAudio fileId={msg.text} size="compact" /> : null;
              case 'document':
                return msg.text ? (
                  <ChatDocument
                    fileId={msg.text}
                    filename={msg.filename || t('common.view')}
                    size="compact"
                  />
                ) : null;
              default:
                return <ChatMessage message={msg.text || ''} />;
            }
          }
          return <ChatMessage message={msg.text || ''} />;
        case 'sticker':
          return msg.text ? <ChatSticker url={msg.text} size="compact" /> : null;
        case 'audio':
          return msg.text ? <ChatAudio fileId={msg.text} size="compact" /> : null;
        case 'video':
          return msg.text ? <ChatVideo fileId={msg.text} size="compact" /> : null;
        case 'image':
          return msg.text ? <ChatImage url={msg.text} caption={msg.caption || undefined} size="compact" /> : null;
        case 'document':
          return msg.text ? (
            <ChatDocument
              fileId={msg.text}
              caption={msg.caption || undefined}
              filename={msg.filename || t('common.view')}
              size="compact"
            />
          ) : null;
        case 'location':
          return msg.text ? <ChatLocation latLong={msg.text} size="compact" /> : null;
        default:
          return null;
      }
    })();

    return (
      <Box
        mb={1}
        p={1.5}
        borderRadius={1}
        bgcolor={"rgba(255, 255, 255, 0.8)"}
        borderLeft={`3px solid ${theme.palette.primary.main}`}
      >
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {sender}
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          {preview}
        </Box>
      </Box>
    );
  };

  return (
    <Box display="flex" justifyContent={presentation.alignment} mb={1}>
      <Box textAlign={presentation.textAlignment} maxWidth="100%">
        <Box
          display="flex"
          alignItems="start"
          p={2}
          mr={1}
          gap={1}
          borderRadius={2}
          bgcolor={isUnknown ? "grey.100" : (isInbound ? "common.white" : (backgroundColor || theme.palette.primary.states?.focus))}
          color={isOutbound ? (textColor || "text.primary") : "text.primary"}
          sx={{
            border: isUnknown ? 1 : 0,
            borderColor: isUnknown ? 'divider' : undefined,
            borderTopLeftRadius: isInbound ? 0 : 2,
            borderTopRightRadius: isOutbound ? 0 : 2,
          }}
        >
          <Box
            sx={{
              wordBreak: 'break-word',
              textWrap: 'auto',
              whiteSpace: 'balance',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              gap: 1,
              flexDirection: 'column',
              alignItems: 'flex-start',
              display: 'flex',
            }}
          >
            <Box display="flex" flexDirection="row" gap={1} justifyContent="space-between" width="100%">
              <Box display="flex" flexDirection="row" gap={1}>
                {isUnknown && (
                  <Chip size="small" variant="outlined" label={t('whatsapp.unknownDirection')} />
                )}
                {cases?.map(item => (
                  <Chip 
                    size="small"
                    key={item} 
                    color="secondary" 
                    label={addLeadingZeros(item, defaultLeadingZeros)} 
                  />

                ))}
              </Box>
              <Box display="flex" alignItems="center">
                <IconButton
                  onClick={handleMenuClick}
                  color={isOutbound ? "inherit" : "default"}
                  size="small"
                  >
                  <MoreVert sx={{ fontSize: 18 }} />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleMenuClose}
                  MenuListProps={{ dense: true }}
                >
                  <MenuItem onClick={handleReplyMessage}>
                    {t('whatsapp.replyToThisMessage')}
                  </MenuItem>
                  {casesData && casesData.items.length > 0 && (
                    <Box sx={{ px: 2, py: 1, width: '100%', maxHeight: 280, overflowY: 'auto' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        {t('whatsapp.linkedCases')}
                      </Typography>
                      {casesData.items.map((caseItem: CaseResponseDto) => {
                        const isLinked = selectedCaseIds.includes(caseItem.id);
                        const isDisabled = isPending(caseItem.id);
                        const isCurrentCase = currentCaseId === caseItem.id;
                        return (
                          <MenuItem
                            key={caseItem.id}
                            disableRipple
                            disabled={isDisabled}
                            sx={{
                              backgroundColor: isCurrentCase ? theme.palette.grey[200] : undefined,
                              borderLeft: isCurrentCase ? `2px solid ${theme.palette.grey[500]}` : undefined,
                            }}
                            onClick={(event) => {
                              event.stopPropagation();
                              onCaseToggle(caseItem.id);
                            }}
                          >
                            <Checkbox
                              checked={isLinked}
                              onClick={(event) => event.stopPropagation()}
                              onChange={() => onCaseToggle(caseItem.id)}
                              disabled={isDisabled}
                            />
                            <ListItemText primary={`#${caseItem.id} - ${caseItem.name}`} />
                            {isPending(caseItem.id) ? (
                              <CircularProgress size={14} />
                            ) : null}
                          </MenuItem>
                        );
                      })}
                    </Box>
                  )}
                </Menu>
              </Box>
            </Box>
            {renderContextMessage()}
            {children}
          </Box>
        </Box>

        <Box px={2} display="flex" flexDirection="row-reverse">
          <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
            {isOutbound && status && `${t(`whatsapp.messageStatus.${status}` as const)} · `}{time}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}; 
